import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
    Store,
    User,
    Mail,
    Lock,
    Globe,
    FileSpreadsheet,
    ArrowRight,
    CheckCircle2,
    Loader2,
    ShieldCheck,
    ChevronLeft,
    Rocket,
    Copy,
    ExternalLink
} from 'lucide-react';

const CreateStore = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { createStore } = useAuth();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);

    const serviceEmail = "orders-data@orders-sync-485111.iam.gserviceaccount.com";

    const handleCopyEmail = () => {
        navigator.clipboard.writeText(serviceEmail);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const [formData, setFormData] = useState({
        brand: {
            name: '',
            slug: '',
            sheetUrl: '',
            sheetName: ''
        },
        owner: {
            name: '',
            email: '',
            password: ''
        }
    });

    const handleBrandChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            brand: { ...prev.brand, [name]: value }
        }));
    };

    const handleOwnerChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            owner: { ...prev.owner, [name]: value }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);

        const result = await createStore(formData);

        if (result.success) {
            navigate('/');
        } else {
            setError(result.message);
            setIsSubmitting(false);
        }
    };

    const generateSlug = () => {
        const slug = formData.brand.name
            .toLowerCase()
            .replace(/ /g, '-')
            .replace(/[^\w-]+/g, '');
        setFormData(prev => ({
            ...prev,
            brand: { ...prev.brand, slug }
        }));
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC] p-4 md:p-8 lg:p-12 overflow-x-hidden">
            <div className="max-w-6xl mx-auto">
                <button
                    onClick={() => navigate('/landing')}
                    className="flex items-center gap-2 text-gray-400 hover:text-gray-900 font-black mb-6 md:mb-10 transition-colors group text-[10px] md:text-sm uppercase tracking-widest"
                >
                    <ChevronLeft className="group-hover:-translate-x-1 transition-transform" size={18} />
                    {t('create_store.back')}
                </button>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                    {/* Left Column: Info */}
                    <div className="lg:col-span-4 space-y-6 md:space-y-8">
                        <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
                            <div className="w-14 h-14 md:w-16 md:h-16 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-100 mb-4 md:mb-6">
                                <Store className="text-white w-7 h-7 md:w-8 md:h-8" />
                            </div>
                            <h1 className="text-2xl md:text-4xl font-black text-gray-900 tracking-tight mb-2 md:mb-4 whitespace-pre-line">{t('create_store.title')}</h1>
                            <p className="text-xs md:text-base text-gray-500 font-medium leading-relaxed max-w-sm lg:max-w-none">
                                {t('create_store.subtitle')}
                            </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4 md:gap-6">
                            {[
                                { icon: ShieldCheck, title: t('create_store.features.data_isolation.title'), desc: t('create_store.features.data_isolation.desc') },
                                { icon: Rocket, title: t('create_store.features.logistics.title'), desc: t('create_store.features.logistics.desc') },
                                { icon: FileSpreadsheet, title: t('create_store.features.sync.title'), desc: t('create_store.features.sync.desc') }
                            ].map((item, i) => (
                                <div key={i} className="flex gap-4 p-4 bg-white border border-gray-100 rounded-2xl lg:bg-transparent lg:border-none lg:p-0">
                                    <div className="mt-0.5 w-10 h-10 shrink-0 bg-white border border-gray-100 rounded-xl flex items-center justify-center text-blue-600 shadow-sm">
                                        <item.icon size={20} />
                                    </div>
                                    <div className="py-0.5">
                                        <h4 className="font-bold text-gray-900 text-xs md:text-sm">{item.title}</h4>
                                        <p className="text-[10px] md:text-xs text-gray-400 font-medium leading-tight mt-0.5">{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right Column: Form */}
                    <div className="lg:col-span-8">
                        <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8" autoComplete="off">
                            {/* Brand Settings Section */}
                            <section className="bg-white rounded-3xl md:rounded-[2.5rem] border border-gray-100 shadow-sm p-6 md:p-8 lg:p-10">
                                <h3 className="text-lg md:text-xl font-black text-gray-900 mb-6 md:mb-8 flex items-center gap-3">
                                    <span className="w-1.5 h-6 bg-blue-600 rounded-full" />
                                    {t('create_store.sections.store')}
                                </h3>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1 block">{t('create_store.form.brand_name')}</label>
                                        <div className="relative group">
                                            <Store className="absolute left-4 md:left-5 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-blue-500 transition-colors w-4 h-4 md:w-5 md:h-5" />
                                            <input
                                                required
                                                type="text"
                                                name="name"
                                                className="w-full pl-11 md:pl-14 pr-6 py-4 md:py-5 bg-gray-50 border border-gray-100 rounded-2xl md:rounded-3xl focus:outline-none focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 font-bold text-gray-800 text-xs md:text-base transition-all"
                                                placeholder={t('create_store.form.brand_name_placeholder')}
                                                value={formData.brand.name}
                                                onChange={handleBrandChange}
                                                onBlur={generateSlug}
                                                autoComplete="off"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1 block">{t('create_store.form.slug')}</label>
                                        <div className="relative group">
                                            <Globe className="absolute left-4 md:left-5 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-blue-500 transition-colors w-4 h-4 md:w-5 md:h-5" />
                                            <input
                                                required
                                                type="text"
                                                name="slug"
                                                className="w-full pl-11 md:pl-14 pr-6 py-4 md:py-5 bg-gray-50 border border-gray-100 rounded-2xl md:rounded-3xl focus:outline-none focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 font-bold text-gray-800 text-xs md:text-base transition-all lowercase"
                                                placeholder={t('create_store.form.slug_placeholder')}
                                                value={formData.brand.slug}
                                                onChange={handleBrandChange}
                                                autoComplete="off"
                                            />
                                        </div>
                                    </div>

                                    <div className="md:col-span-2 space-y-5 md:space-y-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                                            <div className="md:col-span-2 space-y-2">
                                                <label className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1 block">{t('create_store.form.sheet_url')}</label>
                                                <div className="relative group">
                                                    <FileSpreadsheet className="absolute left-4 md:left-5 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-blue-500 transition-colors w-4 h-4 md:w-5 md:h-5" />
                                                    <input
                                                        required
                                                        type="url"
                                                        name="sheetUrl"
                                                        className="w-full pl-11 md:pl-14 pr-6 py-4 md:py-5 bg-gray-50 border border-gray-100 rounded-2xl md:rounded-3xl focus:outline-none focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 font-bold text-gray-800 transition-all text-[10px] md:text-sm"
                                                        placeholder="https://docs.google.com/spreadsheets/d/..."
                                                        value={formData.brand.sheetUrl}
                                                        onChange={handleBrandChange}
                                                        autoComplete="off"
                                                    />
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1 block">{t('create_store.form.sheet_name')}</label>
                                                <div className="relative group">
                                                    <FileSpreadsheet className="absolute left-4 md:left-5 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-blue-500 transition-colors w-4 h-4 md:w-5 md:h-5" />
                                                    <input
                                                        required
                                                        type="text"
                                                        name="sheetName"
                                                        className="w-full pl-11 md:pl-14 pr-6 py-4 md:py-5 bg-gray-50 border border-gray-100 rounded-2xl md:rounded-3xl focus:outline-none focus:ring-4 focus:ring-blue-500/5 focus:border-blue-500 font-bold text-gray-800 transition-all text-xs md:text-sm"
                                                        placeholder={t('create_store.form.sheet_name_placeholder')}
                                                        value={formData.brand.sheetName}
                                                        onChange={handleBrandChange}
                                                        autoComplete="off"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Template & Format Guide */}
                                        <div className="bg-blue-50/50 rounded-3xl md:rounded-[2rem] p-5 md:p-8 border border-blue-100 space-y-6 md:space-y-10">
                                            {/* Step 1: Authorization */}
                                            <div className="space-y-4">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-12 h-12 shrink-0 bg-blue-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-blue-100">
                                                        <ShieldCheck size={24} />
                                                    </div>
                                                    <div className="flex-1">
                                                        <h4 className="font-black text-xs md:text-base text-blue-900 uppercase tracking-tight">{t('create_store.sync_guide.share_title')}</h4>
                                                        <p className="text-[10px] md:text-xs font-bold text-blue-400 uppercase tracking-widest leading-none mt-1">{t('create_store.sync_guide.share_msg')}</p>
                                                    </div>
                                                </div>

                                                <div className="bg-white border border-blue-100 rounded-[1.25rem] md:rounded-2xl p-4 md:p-5 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-sm group hover:border-blue-300 transition-colors">
                                                    <code className="text-xs md:text-sm font-bold text-blue-600 truncate select-all px-2 py-1 md:py-0 bg-blue-50/50 md:bg-transparent rounded-lg w-full sm:w-auto text-center sm:text-left">{serviceEmail}</code>
                                                    <button
                                                        type="button"
                                                        onClick={handleCopyEmail}
                                                        className={`w-full sm:w-auto shrink-0 flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-[10px] md:text-xs font-black uppercase tracking-widest transition-all ${copied ? 'bg-green-500 text-white' : 'bg-blue-600 text-white hover:bg-black shadow-lg shadow-blue-100'
                                                            }`}
                                                    >
                                                        {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                                                        {copied ? t('create_store.sync_guide.share_copied') : t('create_store.sync_guide.share_copy')}
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Divider */}
                                            <div className="relative">
                                                <div className="absolute inset-x-0 top-1/2 border-t border-blue-100/50" />
                                                <div className="relative flex justify-center">
                                                    <span className="bg-white px-4 py-1 rounded-full border border-blue-50 text-[10px] md:text-xs font-black text-blue-300 uppercase tracking-widest">+</span>
                                                </div>
                                            </div>

                                            {/* Step 2: Template & Format */}
                                            <div className="space-y-6 md:space-y-8">
                                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-12 h-12 shrink-0 bg-blue-100 rounded-2xl flex items-center justify-center text-blue-600 border border-blue-200">
                                                            <FileSpreadsheet size={24} />
                                                        </div>
                                                        <div>
                                                            <h4 className="font-black text-xs md:text-base text-blue-900 uppercase tracking-tight">{t('create_store.sync_guide.title')}</h4>
                                                            <p className="text-[10px] md:text-xs font-bold text-blue-400 uppercase tracking-widest leading-none mt-1">{t('create_store.sync_guide.subtitle')}</p>
                                                        </div>
                                                    </div>
                                                    <a
                                                        href="https://docs.google.com/spreadsheets/d/1aa7nurkzHLBtwMtPleXWi7xE0wEN9-mlhRLApV58nqo/edit?usp=sharing"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center justify-center gap-3 bg-white border-2 border-blue-600 text-blue-600 px-6 py-4 rounded-2xl font-black text-[10px] md:text-xs hover:bg-blue-600 hover:text-white transition-all shadow-sm w-full md:w-auto group"
                                                    >
                                                        {t('create_store.sync_guide.open_template')}
                                                        <ExternalLink className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" size={16} />
                                                    </a>
                                                </div>

                                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                                                    {/* Rules List */}
                                                    <div className="space-y-4">
                                                        <p className="text-[10px] md:text-xs text-blue-800 font-bold uppercase tracking-wide">
                                                            {t('create_store.sync_guide.rules_intro')}
                                                        </p>
                                                        <ul className="grid grid-cols-1 gap-2.5">
                                                            {(t('create_store.sync_guide.rules', { returnObjects: true }) || []).map((rule, i) => (
                                                                <li key={i} className="flex items-start gap-3 text-[10px] md:text-xs font-medium text-blue-700/80">
                                                                    <CheckCircle2 className="shrink-0 mt-0.5 w-3.5 h-3.5 text-blue-400" />
                                                                    {rule}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>

                                                    {/* Visual Preview (CSS Table) */}
                                                    <div className="space-y-4">
                                                        <p className="text-[9px] md:text-[10px] font-black text-blue-300 uppercase tracking-widest text-right">{t('create_store.sync_guide.preview')}</p>
                                                        <div className="bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden overflow-x-auto ring-4 ring-blue-50/50">
                                                            <table className="w-full text-[9px] md:text-[10px] border-collapse min-w-[500px]">
                                                                <thead>
                                                                    <tr className="bg-gray-50 border-b border-gray-100">
                                                                        {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'].map(col => (
                                                                            <th key={col} className="p-1.5 px-2 font-black text-gray-300 border-r border-gray-100 text-center">{col}</th>
                                                                        ))}
                                                                    </tr>
                                                                    <tr className="border-b border-gray-100 italic bg-blue-50/20">
                                                                        {['Order ID', 'Date', 'Name', 'Phone', 'City', 'Address', 'Product name', 'Product variant', 'Price', 'Quantity', 'utm_source', 'product_url'].map(h => (
                                                                            <th key={h} className="p-2.5 px-4 font-bold text-blue-600 border-r border-gray-100 text-left whitespace-nowrap">{h}</th>
                                                                        ))}
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    <tr className="opacity-60">
                                                                        {['1001', '06/02', 'Ahmed', '0661...', 'Casablanca', 'Centre ville...', 'Product name', 'XL/blue', '399DH', '3', 'Facebook', 'www...'].map((val, i) => (
                                                                            <td key={i} className="p-2.5 px-4 text-gray-400 border-r border-gray-50 whitespace-nowrap font-medium">{val}</td>
                                                                        ))}
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                        <p className="text-[9px] text-blue-300 font-bold italic text-right">
                                                            {t('create_store.sync_guide.footer')}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            {/* Owner Account Section */}
                            <section className="bg-white rounded-3xl md:rounded-[2.5rem] border border-gray-100 shadow-sm p-6 md:p-8 lg:p-10">
                                <h3 className="text-lg md:text-xl font-black text-gray-900 mb-6 md:mb-8 flex items-center gap-3">
                                    <span className="w-1.5 h-6 bg-indigo-600 rounded-full" />
                                    {t('create_store.sections.owner')}
                                </h3>

                                <div className="space-y-5 md:space-y-6">
                                    <div className="space-y-2">
                                        <label className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1 block">{t('create_store.form.full_name')}</label>
                                        <div className="relative group">
                                            <User className="absolute left-4 md:left-5 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-500 transition-colors w-4 h-4 md:w-5 md:h-5" />
                                            <input
                                                required
                                                type="text"
                                                name="name"
                                                className="w-full pl-11 md:pl-14 pr-6 py-4 md:py-5 bg-gray-50 border border-gray-100 rounded-2xl md:rounded-3xl focus:outline-none focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500 font-bold text-gray-800 text-xs md:text-base transition-all"
                                                placeholder={t('create_store.form.owner_name_placeholder')}
                                                value={formData.owner.name}
                                                onChange={handleOwnerChange}
                                                autoComplete="off"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                                        <div className="space-y-2">
                                            <label className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1 block">{t('create_store.form.email')}</label>
                                            <div className="relative group">
                                                <Mail className="absolute left-4 md:left-5 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-500 transition-colors w-4 h-4 md:w-5 md:h-5" />
                                                <input
                                                    required
                                                    type="email"
                                                    name="email"
                                                    className="w-full pl-11 md:pl-14 pr-6 py-4 md:py-5 bg-gray-50 border border-gray-100 rounded-2xl md:rounded-3xl focus:outline-none focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500 font-bold text-gray-800 text-xs md:text-base transition-all"
                                                    placeholder={t('create_store.form.email_placeholder')}
                                                    value={formData.owner.email}
                                                    onChange={handleOwnerChange}
                                                    autoComplete="off"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1 block">{t('create_store.form.password')}</label>
                                            <div className="relative group">
                                                <Lock className="absolute left-4 md:left-5 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-500 transition-colors w-4 h-4 md:w-5 md:h-5" />
                                                <input
                                                    required
                                                    type="password"
                                                    name="password"
                                                    className="w-full pl-11 md:pl-14 pr-6 py-4 md:py-5 bg-gray-50 border border-gray-100 rounded-2xl md:rounded-3xl focus:outline-none focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500 font-bold text-gray-800 text-xs md:text-base transition-all"
                                                    placeholder="••••••••"
                                                    value={formData.owner.password}
                                                    onChange={handleOwnerChange}
                                                    autoComplete="new-password"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            <div className="pt-2">
                                {error && (
                                    <div className="mb-5 md:mb-6 p-4 md:p-5 bg-red-50 border border-red-100 rounded-2xl md:rounded-3xl text-red-600 font-bold text-[10px] md:text-xs flex items-center gap-3 text-left">
                                        <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 text-red-600 font-black text-xs">!</div>
                                        {error}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full py-4 md:py-6 bg-gray-900 hover:bg-black text-white rounded-2xl md:rounded-[2.5rem] font-bold transition-all shadow-2xl shadow-gray-200 flex items-center justify-center gap-3 tracking-tight text-base md:text-lg disabled:opacity-50 group px-6"
                                >
                                    {isSubmitting ? (
                                        <Loader2 className="animate-spin" size={24} />
                                    ) : (
                                        <>
                                            <span className="truncate">{t('create_store.form.submit')}</span>
                                            <ArrowRight className="group-hover:translate-x-1 transition-transform shrink-0 w-5 h-5 md:w-6 md:h-6" />
                                        </>
                                    )}
                                </button>

                                <p className="text-center mt-4 md:mt-6 text-[9px] md:text-xs text-gray-400 font-bold uppercase tracking-[0.15em] leading-relaxed">
                                    {t('create_store.form.terms')}
                                </p>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateStore;
